import React from "react";
import ShareCardFrame, { CardFooter, Wordmark, scoreColor } from "@/components/share/ShareCardFrame";
import { TRAITS } from "@/lib/traits";
import { supervisorCaptions } from "@/components/share/CaptionBox";

const FOOTER = "Verified via my360boss.com. Cryptographically secure. 100% anonymous worker data.";

export default function SupervisorStatCard({ sup }) {
  const ranked = [...TRAITS].map((t) => ({ ...t, score: sup.traits?.[t.key] || 0 })).filter((t) => t.score > 0).sort((a, b) => b.score - a.score);
  const showTop = sup.overall >= 3;
  const traits = showTop ? ranked.slice(0, 3) : ranked.slice(-3).reverse();
  const color = scoreColor(sup.overall);
  const url = `${window.location.origin}/supervisor/${sup.id}`;

  return (
    <ShareCardFrame fileName={`my360boss-${sup.name.replace(/\s+/g, "-").toLowerCase()}`} shareText={`${sup.name} (${sup.company}) scores ${sup.overall}/5 on my360boss — ${sup.review_count} anonymous reviews. ${url}`} shareUrl={url} captions={supervisorCaptions(sup, url)} testid="supervisor-stat-card">
      <div className="px-6 pt-6 flex items-center justify-between">
        <Wordmark />
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>{sup.review_count} reviews</span>
      </div>

      <div className="px-6 mt-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="leading-[0.95] break-words" style={{ fontSize: "2.4rem", fontWeight: 900, letterSpacing: "-0.02em" }} data-testid="stat-card-name">{sup.name}</h2>
          <div className="mt-3 text-sm leading-snug" style={{ color: "#94A3B8" }}>
            <div className="font-semibold" style={{ color: "#CBD5E1" }}>{sup.company}</div>
            <div>{sup.title}</div>
            {sup.location && <div>{sup.location}</div>}
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-center justify-center text-slate-900" style={{ width: 118, height: 118, backgroundColor: color, clipPath: "polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)" }} data-testid="stat-card-score-badge">
          <span style={{ fontSize: "2.1rem", fontWeight: 900, lineHeight: 1 }}>{Number(sup.overall).toFixed(1)}</span>
          <span className="text-[11px] font-bold tracking-wider">/ 5 ★</span>
        </div>
      </div>

      <div className="px-6 mt-6">
        <p className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: showTop ? "#10B981" : "#EF4444" }}>{showTop ? "Highest-rated traits" : "Lowest-rated traits"}</p>
        <div className="grid grid-cols-3 gap-2" data-testid="stat-card-traits">
          {traits.map((t) => (
            <div key={t.key} className="rounded-lg px-2.5 py-2.5" style={{ backgroundColor: "rgba(248,250,252,0.06)", border: "1px solid rgba(248,250,252,0.12)" }}>
              <div style={{ fontSize: "1.35rem", fontWeight: 800, color: scoreColor(t.score), lineHeight: 1 }}>{t.score.toFixed(1)}</div>
              <div className="text-[11px] mt-1 leading-tight" style={{ color: "#CBD5E1" }}>{t.short}</div>
            </div>
          ))}
        </div>
        {sup.top_tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="stat-card-tags">
            {sup.top_tags.map((t) => (
              <span key={t.tag} className="text-[11px] font-bold px-3 py-1 rounded-full" style={{ backgroundColor: "#EF4444", color: "#fff" }}>[{t.tag} ×{t.count}]</span>
            ))}
          </div>
        )}
        <div className="mt-5 rounded-xl px-4 py-3 flex items-center justify-between" style={{ backgroundColor: "rgba(248,250,252,0.06)", border: "1px solid rgba(248,250,252,0.12)" }}>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>Would recommend</p>
            <p style={{ fontSize: "1.6rem", fontWeight: 900, lineHeight: 1.1, color: scoreColor(sup.recommend_rate / 20) }}>{sup.recommend_rate}%</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>Open to Feedback</p>
            <p style={{ fontSize: "1.6rem", fontWeight: 900, lineHeight: 1.1, color: scoreColor(sup.traits?.open_to_feedback || 0) }}>{sup.traits?.open_to_feedback ? sup.traits.open_to_feedback.toFixed(1) : "—"}</p>
          </div>
        </div>
      </div>

      <CardFooter text={FOOTER} />
    </ShareCardFrame>
  );
}
